import { useEffect } from 'react';

import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import Box from '@zenbusiness/zui/Box';
import Heading from '@zenbusiness/zui/Heading';
import Link from '@zenbusiness/zui/Link';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import { FocusGrid, ContainerGrid } from '#client/components/Grid';
import { RELEASE_THREAD_BANK_COMPLIANCE } from '#client/constants/experimentsFlags';
import useBreakpoints from '#client/hooks/useBreakpoints';
import useSkeleton from '#client/hooks/useSkeleton';

import { BannerWrapper, Wrapper, PaperStyled } from './components/DataWeHave.styles';
import DataWeHaveSkeleton from './components/DataWeHaveSkeleton';
import { InfoCard } from './components/InfoCard';
import { MissingInfoBanner } from './components/MissingInfoBanner';

import { CONTACT_US_URL } from './constants/contact';
import { ModelMetadata, AccountMembersMetadata } from '../../Metadata/Metadata';

const DataWeHave = ({
  data,
  hasEINOrder = false,
  invalidFields = {
    forBusinessEntity: [],
    forBeneficialOwner: []
  },
  isFirstTime,
  markAsRendered,
  isShowMissingBanner = false
}) => {
  const { forBeneficialOwner, forBusinessEntity } = invalidFields;
  const isThreadBankReleased = useExperimentFlag(RELEASE_THREAD_BANK_COMPLIANCE, false);
  const {
    sizes: { isDesktop }
  } = useBreakpoints();

  const fieldsToRender = () => {
    const fields = [
      ModelMetadata.businessEntityType,
      ModelMetadata.ein,
      ...(isThreadBankReleased ? [ModelMetadata.sourceOfFunds] : []),
      ...(isThreadBankReleased ? [ModelMetadata.usNexus] : [])
    ];
    // No EIN order and No EIN number => hide EIN field
    if (!hasEINOrder && !data.ein) {
      return fields.filter((field) => field !== ModelMetadata.ein);
    }
    return fields;
  };

  // when component init, the skeleton will be show
  const [showSkeleton] = useSkeleton(true, 500);

  useEffect(() => {
    // when the passed the timeout and the flag still hold :true for the first time render
    // we should mark the component as rendered
    if (!showSkeleton && isFirstTime) {
      markAsRendered();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSkeleton, isFirstTime]);

  return isFirstTime ? (
    <DataWeHaveSkeleton />
  ) : (
    <ContainerGrid>
      <FocusGrid>
        {isShowMissingBanner && (
          <BannerWrapper>
            <MissingInfoBanner />
          </BannerWrapper>
        )}
        <Wrapper
          paddingBottom="48px"
          paddingTop="48px"
        >
          <Heading
            family="secondary"
            size="3xl"
            weight="bold"
          >
            Let&apos;s get you ready to bank!
            <br />
            If this info looks good, hit Next.
          </Heading>
          <Box marginTop={isDesktop ? '16px' : '12px'}>
            <Text
              color="secondary"
              component="p"
              size="lg"
            >
              Since we already had your info, we saved you some time. If something&apos;s not right,
              email{' '}
              <Link
                href={CONTACT_US_URL}
                rel="noopener noreferrer"
                target="_blank"
                underline="none"
              >
                moneysupport@zenbusiness.com
              </Link>{' '}
              or call{' '}
              <Link
                href="tel:18444936249"
                underline="none"
              >
                1 (844) 4-ZENBIZ
              </Link>
              .
            </Text>
          </Box>
          <PaperStyled
            elevation={0}
            variant="outlined"
          >
            <InfoCard
              ariaLabel="Business Details"
              data={data}
              icon={
                <SharedAsset
                  alt=""
                  height={28}
                  src="/icon/misc/bullet-business.svg"
                  width={28}
                />
              }
              invalidFields={forBusinessEntity}
              key="business-details"
              leftFields={[
                ModelMetadata.businessEntityName,
                ModelMetadata.businessAddress,
                ModelMetadata.phone,
                ModelMetadata.businessWebsiteUrl,
                ...(isThreadBankReleased ? [ModelMetadata.businessPurpose] : []),
                ...(isThreadBankReleased ? [ModelMetadata.regulatedStatus] : []),
                ...(isThreadBankReleased ? [ModelMetadata.transactionVolume] : [])
              ]}
              rightFields={fieldsToRender()}
              title="Business Details"
            />
            <InfoCard
              ariaLabel="Account Owner"
              data={data?.bankAccountMembers?.some(() => true) ? data.bankAccountMembers[0] : {}}
              icon={
                <SharedAsset
                  alt=""
                  height={28}
                  src="/icon/misc/bullet-owner.svg"
                  width={28}
                />
              }
              invalidFields={forBeneficialOwner}
              key="account-owner"
              leftFields={[
                AccountMembersMetadata.fullName,
                AccountMembersMetadata.address,
                AccountMembersMetadata.dateOfBirth,
                AccountMembersMetadata.ssnLast
              ]}
              rightFields={[
                AccountMembersMetadata.role,
                AccountMembersMetadata.email,
                AccountMembersMetadata.phone
              ]}
              title="Account Owner"
            />
          </PaperStyled>
        </Wrapper>
      </FocusGrid>
    </ContainerGrid>
  );
};

DataWeHave.propTypes = {
  data: PropTypes.shape({
    bankAccountMembers: PropTypes.arrayOf(PropTypes.shape()),
    ein: PropTypes.string
  }).isRequired,
  hasEINOrder: PropTypes.bool,
  invalidFields: PropTypes.shape({
    forBusinessEntity: PropTypes.arrayOf(PropTypes.string),
    forBeneficialOwner: PropTypes.arrayOf(PropTypes.string)
  }),
  isFirstTime: PropTypes.bool.isRequired,
  markAsRendered: PropTypes.func.isRequired,
  isShowMissingBanner: PropTypes.bool
};

export default DataWeHave;
